import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';

interface UkassaStatus { ok: boolean; cashboxName?: string; shiftOpen?: boolean; shiftText?: string; tokenExpires?: string; userName?: string; error?: string; }
interface MontBalance { available?: number; blocked?: boolean; blockReason?: string; error?: string; }

interface Today {
  generatedAt: string;
  kpi: {
    revenueToday: number; revenueYesterday: number; wonToday: number; wonYesterday: number; avgCheck: number;
    createdToday: number; createdYesterday: number; lostToday: number; active: number; activeAmount: number;
  };
  payments: {
    total: number; totalYesterday: number; count: number;
    byMethod: { method: string; today: number; count: number }[];
    recent: { id: number; amount: number; method: string; at: string; dealId: number; number: string; title: string }[];
  };
  purchases?: { total: number; totalYesterday: number; docs: number; net: number; bySupplier: { id: number | null; name: string; docs: number; amount: number }[] };
  pipelines: { id: number; name: string; count: number; amount: number; stages: { id: number; name: string; count: number; amount: number }[] }[];
  managers: { id: number; name: string; created: number; won: number; revenue: number; active: number }[];
  stale: { total: number; items: { id: number; number: string; title: string; amount: number; stage: string; days: number }[] };
  works: {
    plannedToday: number; doneToday: number; inProgress: number; overdue: number; unscheduled: number;
    items: { id: number; number: string; title: string; status: string; kind: string; assignee: string | null; scheduledAt: string | null; completedAt: string | null; overdue: boolean }[];
  };
  calls: { today: number; yesterday: number; unknown: number; recent: { id: number; phone: string; client: string | null; clientId: number | null; startedAt: string; seconds: number | null }[] };
  site: { pending: number; pendingAmount: number; pendingRecent: number; createdToday: number; completedToday: number; completedAmount: number; failedToday: number };
  stock: { low: { id: number; name: string; available: number; min: number }[]; keys: { id: number; name: string; available: number; total: number }[] };
  debts: { count: number; amount: number; oldestDays: number };
  halyk: {
    total24h: number; errors24h: number; lastOk: string | null;
    lastError: { kind: string; message: string; at: string } | null;
  } | null;
  awaiting: {
    count: number; amount: number;
    byCompany: { company: string; count: number; amount: number; oldest: string | null; oldestDays: number }[];
    items: { id: number; number: string; title: string; amount: number; stage: string; company: string; period: string | null; days: number }[];
  };
  lists: {
    won: { id: number; number: string; title: string; amount: number; at: string }[];
    created: { id: number; number: string; title: string; amount: number; at: string; stage: string | null }[];
  };
}

const TZ = 'Asia/Almaty';
const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '16px 18px', minWidth: 0 };
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 };
const muted = '#6b7280';

const fmt = (n: number) => Math.round(n || 0).toLocaleString('ru-RU');
const money = (n: number) => `${fmt(n)} ₸`;
const time = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }) : '—');
const METHODS: Record<string, string> = { cash: 'Наличные', card: 'Карта', kaspi: 'Kaspi', transfer: 'Перевод', halyk: 'Halyk' };
const WORK_STATUS: Record<string, { t: string; c: string }> = {
  new: { t: 'Новая', c: '#60a5fa' }, in_progress: { t: 'В работе', c: '#fbbf24' }, done: { t: 'Выполнена', c: '#4ade80' }, cancelled: { t: 'Отменена', c: '#9ca3af' },
};

function Dot({ ok }: { ok: boolean }) {
  const c = ok ? '#4ade80' : '#f87171';
  return <span style={{ width: 9, height: 9, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0, boxShadow: `0 0 7px ${c}` }} />;
}

function Delta({ cur, prev, suffix = 'вчера' }: { cur: number; prev: number; suffix?: string }) {
  if (!prev && !cur) return <span style={{ color: muted, fontSize: 12 }}>как {suffix}</span>;
  if (!prev) return <span style={{ color: '#4ade80', fontSize: 12 }}>▲ новый день</span>;
  const p = Math.round(((cur - prev) / prev) * 100);
  const up = p >= 0;
  return <span style={{ color: p === 0 ? muted : up ? '#4ade80' : '#f87171', fontSize: 12 }}>{p === 0 ? '＝' : up ? '▲' : '▼'} {Math.abs(p)}% к {suffix} ({fmt(prev)})</span>;
}

function Badge({ text, color }: { text: string; color: string }) {
  return <span style={{ fontSize: 11, fontWeight: 700, color, border: `1px solid ${color}55`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>{text}</span>;
}

function Row({ left, right, sub }: { left: React.ReactNode; right?: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 0', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#e5e7eb', overflow: 'hidden', textOverflow: 'ellipsis' }}>{left}</div>
        {sub && <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>{sub}</div>}
      </div>
      {right !== undefined && <div style={{ fontSize: 13, color: '#d1d5db', textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

const Empty = ({ text }: { text: string }) => <div style={{ color: '#4b5563', fontSize: 13, padding: '6px 0' }}>{text}</div>;

function Kpi({ label, value, color, children }: { label: string; value: React.ReactNode; color: string; children?: React.ReactNode }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 11, color: muted, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1, marginBottom: 8 }}>{value}</div>
      <div style={{ fontSize: 12, color: muted, display: 'flex', flexDirection: 'column', gap: 3 }}>{children}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState<Today | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedTab, setFeedTab] = useState<'won' | 'created' | 'pay'>('won');
  const [auto, setAuto] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const [ukassa, setUkassa] = useState<UkassaStatus | null>(null);
  const [mont, setMont] = useState<MontBalance | null>(null);
  const [ukassaLoading, setUkassaLoading] = useState(false);
  const [montLoading, setMontLoading] = useState(false);

  const loadToday = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/today');
      setData(r.data); setError(null); setLastUpdated(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Не удалось загрузить данные');
    }
    setLoading(false);
  }, []);

  const loadUkassa = useCallback(async () => {
    setUkassaLoading(true);
    try {
      const [testRes, infoRes] = await Promise.allSettled([api.post('/ukassa/test'), api.get('/ukassa/cashbox-info')]);
      const test = testRes.status === 'fulfilled' ? testRes.value.data : null;
      const info = infoRes.status === 'fulfilled' ? infoRes.value.data : null;
      const cb = info?.cashboxes?.[0];
      setUkassa({
        ok: test?.ok === true, cashboxName: cb?.cashbox_name, shiftOpen: cb?.cashbox_mode?.status === 1,
        shiftText: cb?.cashbox_mode?.status_text, tokenExpires: info?.token_expires_at, userName: test?.user,
        error: testRes.status === 'rejected' ? (testRes.reason as any)?.message : undefined,
      });
    } catch (e: any) { setUkassa({ ok: false, error: e.message }); }
    setUkassaLoading(false);
  }, []);

  const loadMont = useCallback(async () => {
    setMontLoading(true);
    try { const r = await api.get('/deals/mont-balance'); setMont(r.data); }
    catch (e: any) { setMont({ error: e.message }); }
    setMontLoading(false);
  }, []);

  useEffect(() => { loadToday(); loadUkassa(); loadMont(); }, [loadToday, loadUkassa, loadMont]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(loadToday, 60000);
    return () => clearInterval(t);
  }, [auto, loadToday]);

  const refreshAll = () => { loadToday(); loadUkassa(); loadMont(); };

  // ── «Требует внимания» ──
  type Alert = { level: 'bad' | 'warn' | 'info'; text: string; to?: string };
  const alerts: Alert[] = [];
  if (data) {
    if (data.works.overdue) alerts.push({ level: 'bad', text: `Просрочено выездов/работ: ${data.works.overdue}`, to: '/work-orders' });
    if (data.debts.count) alerts.push({ level: 'bad', text: `Долги по кассе: ${data.debts.count} на ${money(data.debts.amount)}${data.debts.oldestDays ? `, старейший ${data.debts.oldestDays} дн.` : ''}`, to: '/cash-debts' });
    if (data.halyk) {
      if (data.halyk.errors24h > 0) {
        const le = data.halyk.lastError;
        alerts.push({ level: 'warn', text: `Halyk: ошибок обмена за 24 ч: ${data.halyk.errors24h}${le ? ` (последняя: ${le.kind}, ${time(le.at)} — ${String(le.message).slice(0, 90)})` : ''}`, to: '/settings' });
      }
    }
    if (data.awaiting.count) {
      const oldest = Math.max(...data.awaiting.byCompany.map((c) => c.oldestDays));
      alerts.push({ level: oldest > 60 ? 'warn' : 'info', text: `Ждём оплаты по договорам: ${data.awaiting.count} сделок на ${money(data.awaiting.amount)}${oldest ? `, старейший период ${oldest} дн. назад` : ''}`, to: '/deals' });
    }
    if (data.stale.total) alerts.push({ level: 'warn', text: `Сделки без движения более 3 дней: ${data.stale.total}`, to: '/deals' });
    if (data.site.pendingRecent) alerts.push({ level: 'warn', text: `Заказы сайта ждут оплаты (за 24 ч): ${data.site.pendingRecent}`, to: '/clients?filter=orders' });
    if (data.works.unscheduled) alerts.push({ level: 'info', text: `Работы без назначенного времени: ${data.works.unscheduled}`, to: '/work-orders' });
    if (data.stock.low.length) alerts.push({ level: 'warn', text: `Мало на складе: ${data.stock.low.length} поз.`, to: '/warehouse' });
    if (data.stock.keys.length) alerts.push({ level: 'warn', text: `Заканчиваются ключи активации: ${data.stock.keys.length} поз.`, to: '/products' });
    if (data.calls.unknown) alerts.push({ level: 'info', text: `Звонки сегодня от неизвестных номеров: ${data.calls.unknown}` });
  }
  if (ukassa && !ukassa.error && ukassa.ok && ukassa.shiftOpen === false) alerts.push({ level: 'warn', text: 'Кассовая смена закрыта (UKassa)' });
  if (ukassa && (ukassa.error || ukassa.ok === false)) alerts.push({ level: 'bad', text: 'UKassa не подключена' });
  if (ukassa?.tokenExpires) {
    const d = Math.ceil((new Date(ukassa.tokenExpires).getTime() - Date.now()) / 86400000);
    if (d <= 14) alerts.push({ level: d <= 3 ? 'bad' : 'warn', text: `Токен UKassa истекает через ${d} дн.` });
  }
  if (mont?.blocked) alerts.push({ level: 'bad', text: `Monttech заблокирован: ${mont.blockReason ?? ''}` });
  const lvColor = { bad: '#f87171', warn: '#fbbf24', info: '#60a5fa' };

  const k = data?.kpi;
  const todayStr = new Date().toLocaleDateString('ru-RU', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });

  const feed = !data ? [] : feedTab === 'won'
    ? data.lists.won.map((r) => ({ key: `w${r.id}`, l: `№${r.number} ${r.title}`, r: money(r.amount), s: time(r.at) }))
    : feedTab === 'created'
      ? data.lists.created.map((r) => ({ key: `c${r.id}`, l: `№${r.number} ${r.title}`, r: money(r.amount), s: `${time(r.at)}${r.stage ? ' · ' + r.stage : ''}` }))
      : data.payments.recent.map((r) => ({ key: `p${r.id}`, l: `№${r.number} ${r.title}`, r: money(r.amount), s: `${time(r.at)} · ${METHODS[r.method] ?? r.method}` }));

  return (
    <div className="dashboard-page" style={{ padding: '24px 28px', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <style>{`
        .dg-kpi { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; margin-bottom: 18px; }
        .dg-cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 14px; margin-bottom: 18px; }
        @media (max-width: 480px) { .dg-cols { grid-template-columns: 1fr; } .dg-kpi { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      `}</style>

      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#f9fafb' }}>Дашборд · сегодня</h1>
          <span style={{ fontSize: 12, color: muted }}>{todayStr} · обновлено в {lastUpdated.toLocaleTimeString('ru-RU', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: muted, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} style={{ width: 14, height: 14 }} /> авто-обновление (1 мин)
          </label>
          <button onClick={refreshAll} disabled={loading} style={{ padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#9ca3af', cursor: 'pointer', width: 'auto' }}>
            {loading ? '…' : '↻ Обновить'}
          </button>
        </div>
      </div>

      {error && <div style={{ ...card, borderColor: '#f8717155', color: '#f87171', marginBottom: 18 }}>Не удалось загрузить сводку: {error}</div>}

      {/* KPI */}
      <div className="dg-kpi">
        <Kpi label="Выручка сегодня" color="#60a5fa" value={k ? money(k.revenueToday) : '—'}>
          {k && <><Delta cur={k.revenueToday} prev={k.revenueYesterday} /><span>{k.wonToday} продаж{k.wonToday ? ` · ср. чек ${money(k.avgCheck)}` : ''}</span></>}
        </Kpi>
        <Kpi label="Закуп сегодня" color="#fb923c" value={data?.purchases ? money(data.purchases.total) : '—'}>
          {data?.purchases && <>
            <Delta cur={data.purchases.total} prev={data.purchases.totalYesterday} />
            <span>{data.purchases.docs ? `${data.purchases.docs} док. · ${data.purchases.bySupplier.length} постав.` : 'закупов сегодня нет'}</span>
          </>}
        </Kpi>
        <Kpi label="Выручка − закуп" color={data?.purchases && data.purchases.net < 0 ? '#f87171' : '#4ade80'} value={data?.purchases ? money(data.purchases.net) : '—'}>
          {data?.purchases && k && <span>{money(k.revenueToday)} − {money(data.purchases.total)}</span>}
        </Kpi>
        <Kpi label="Принято платежей" color="#4ade80" value={data ? money(data.payments.total) : '—'}>
          {data && <>
            <Delta cur={data.payments.total} prev={data.payments.totalYesterday} />
            <span>{data.payments.byMethod.length ? data.payments.byMethod.map((m) => `${METHODS[m.method] ?? m.method} ${fmt(m.today)}`).join(' · ') : 'платежей пока нет'}</span>
          </>}
        </Kpi>
        <Kpi label="Новые сделки" color="#f9fafb" value={k?.createdToday ?? '—'}>
          {k && <Delta cur={k.createdToday} prev={k.createdYesterday} />}
        </Kpi>
        <Kpi label="Закрыто сегодня" color="#4ade80" value={k ? <>{k.wonToday}<span style={{ fontSize: 15, color: '#f87171' }}> / {k.lostToday}</span></> : '—'}>
          <span>выиграно / отменено</span>
        </Kpi>
        <Kpi label="В работе" color="#a78bfa" value={k?.active ?? '—'}>
          {k && <span>на сумму {money(k.activeAmount)}</span>}
        </Kpi>
        <Kpi label="Звонки сегодня" color="#fbbf24" value={data?.calls.today ?? '—'}>
          {data && <><Delta cur={data.calls.today} prev={data.calls.yesterday} /><span>неизвестных: {data.calls.unknown}</span></>}
        </Kpi>
        <Kpi label="Заказы сайта" color="#38bdf8" value={data?.site.createdToday ?? '—'}>
          {data && <span>оплачено сегодня: {data.site.completedToday} ({money(data.site.completedAmount)}) · ждут оплаты: {data.site.pending}</span>}
        </Kpi>
        <Kpi label="Ждём оплаты (договоры)" color={data?.awaiting.count ? '#fb923c' : '#9ca3af'} value={data ? money(data.awaiting.amount) : '—'}>
          {data && <span>{data.awaiting.count ? `${data.awaiting.count} сделок · ${data.awaiting.byCompany.length} комп.` : 'нет неоплаченных'}</span>}
        </Kpi>
        <Kpi label="Долги по кассе" color={data?.debts.count ? '#f87171' : '#9ca3af'} value={data ? money(data.debts.amount) : '—'}>
          {data && <span>{data.debts.count ? `${data.debts.count} шт.${data.debts.oldestDays ? `, старейший ${data.debts.oldestDays} дн.` : ''}` : 'нет открытых долгов'}</span>}
        </Kpi>
      </div>

      {/* Закуп у поставщиков */}
      <div style={{ ...card, marginBottom: 18 }}>
        <div style={sectionTitle}>Закуп у поставщиков сегодня</div>
        {!data ? <Empty text="Загрузка…" /> : !data.purchases || data.purchases.bySupplier.length === 0 ? (
          <Empty text="Сегодня закупов у поставщиков не было (считаются проведённые документы прихода)" />
        ) : (
          <>
            {data.purchases.bySupplier.map((s) => (
              <Row key={s.id ?? 'none'} left={s.name} sub={`${s.docs} док. прихода`} right={money(s.amount)} />
            ))}
            <Row left={<b>Итого закуп</b>} right={<b>{money(data.purchases.total)}</b>} />
            <Row left={<b>Выручка за вычетом закупа</b>} right={<b style={{ color: data.purchases.net < 0 ? '#f87171' : '#4ade80' }}>{money(data.purchases.net)}</b>} />
          </>
        )}
      </div>

      {/* Требует внимания */}
      <div style={{ ...card, marginBottom: 18 }}>
        <div style={sectionTitle}>Требует внимания</div>
        {!data && !error ? <Empty text="Загрузка…" /> : alerts.length === 0 ? (
          <div style={{ color: '#4ade80', fontSize: 13 }}>✔ Всё спокойно: просрочек, долгов и проблем с интеграциями нет</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {alerts.map((a, i) => {
              const inner = (<><span style={{ width: 8, height: 8, borderRadius: '50%', background: lvColor[a.level], flexShrink: 0, marginTop: 5 }} /><span style={{ fontSize: 13, color: '#e5e7eb' }}>{a.text}</span>{a.to && <span style={{ marginLeft: 'auto', color: muted, fontSize: 12 }}>→</span>}</>);
              const st: React.CSSProperties = { display: 'flex', gap: 10, padding: '6px 10px', borderRadius: 8, background: `${lvColor[a.level]}12`, textDecoration: 'none' };
              return a.to ? <Link key={i} to={a.to} style={st}>{inner}</Link> : <div key={i} style={st}>{inner}</div>;
            })}
          </div>
        )}
      </div>

      <div className="dg-cols">
        {/* Работы */}
        <div style={card}>
          <div style={sectionTitle}>Выезды и работы</div>
          {data && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <Badge text={`на сегодня ${data.works.plannedToday}`} color="#60a5fa" />
              <Badge text={`в работе ${data.works.inProgress}`} color="#fbbf24" />
              <Badge text={`выполнено ${data.works.doneToday}`} color="#4ade80" />
              {data.works.overdue > 0 && <Badge text={`просрочено ${data.works.overdue}`} color="#f87171" />}
            </div>
          )}
          {!data ? <Empty text="Загрузка…" /> : data.works.items.length === 0 ? <Empty text="На сегодня работ нет" /> :
            data.works.items.slice(0, 8).map((w) => {
              const st = WORK_STATUS[w.status] ?? { t: w.status, c: '#9ca3af' };
              return <Row key={w.id} left={<Link to="/work-orders" style={{ color: 'inherit', textDecoration: 'none' }}>{w.number ? `№${w.number} ` : ''}{w.title}</Link>}
                sub={`${w.scheduledAt ? time(w.scheduledAt) : 'без времени'}${w.assignee ? ' · ' + w.assignee : ''}`}
                right={<Badge text={w.overdue ? 'просрочено' : st.t} color={w.overdue ? '#f87171' : st.c} />} />;
            })}
        </div>

        {/* Менеджеры */}
        <div style={card}>
          <div style={sectionTitle}>Сотрудники сегодня</div>
          {!data ? <Empty text="Загрузка…" /> : data.managers.length === 0 ? <Empty text="Нет данных" /> :
            data.managers.map((m) => (
              <Row key={m.id} left={m.name} sub={`создано ${m.created} · выиграно ${m.won} · в работе ${m.active}`} right={<b style={{ color: m.revenue ? '#60a5fa' : muted }}>{money(m.revenue)}</b>} />
            ))}
        </div>

        {/* Лента */}
        <div style={card}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {([['won', 'Продажи'], ['created', 'Новые'], ['pay', 'Платежи']] as const).map(([id, label]) => (
              <button key={id} onClick={() => setFeedTab(id)} style={{ width: 'auto', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: feedTab === id ? 'rgba(99,102,241,0.25)' : 'transparent', color: feedTab === id ? '#c7d2fe' : '#9ca3af' }}>{label}</button>
            ))}
          </div>
          {!data ? <Empty text="Загрузка…" /> : feed.length === 0 ? <Empty text="Сегодня пока ничего" /> :
            feed.map((f) => <Row key={f.key} left={f.l} sub={f.s} right={f.r} />)}
        </div>

        {/* Звонки */}
        <div style={card}>
          <div style={sectionTitle}>Входящие звонки сегодня</div>
          {!data ? <Empty text="Загрузка…" /> : data.calls.recent.length === 0 ? <Empty text="Звонков пока нет" /> :
            data.calls.recent.map((c) => (
              <Row key={c.id}
                left={c.clientId ? <Link to={`/clients/${c.clientId}`} style={{ color: 'inherit', textDecoration: 'none' }}>{c.client || c.phone}</Link> : <span style={{ color: '#fbbf24' }}>{c.phone || 'Неизвестный номер'}</span>}
                sub={c.client ? c.phone : 'нет в базе клиентов'}
                right={<span>{time(c.startedAt)}{c.seconds !== null ? ` · ${c.seconds >= 60 ? `${Math.floor(c.seconds / 60)} мин ` : ''}${c.seconds % 60} с` : ''}</span>} />
            ))}
        </div>

        {/* Воронки */}
        <div style={{ ...card, gridColumn: 'span 1' }}>
          <div style={sectionTitle}>Сделки в работе по воронкам</div>
          {!data ? <Empty text="Загрузка…" /> : data.pipelines.filter((p) => p.count > 0).length === 0 ? <Empty text="Активных сделок нет" /> :
            data.pipelines.filter((p) => p.count > 0).map((p) => (
              <div key={p.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#d1d5db', marginBottom: 6 }}>
                  <span>{p.name}</span><span>{p.count} · {money(p.amount)}</span>
                </div>
                {p.stages.filter((s) => s.count > 0).map((s) => (
                  <div key={s.id} style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#9ca3af' }}><span>{s.name}</span><span>{s.count}</span></div>
                    <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                      <div style={{ height: 4, borderRadius: 2, background: '#6366f1', width: `${Math.max(4, Math.round((s.count / p.count) * 100))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>

        {/* Ждём оплаты по договорам */}
        <div style={card}>
          <div style={sectionTitle}>Ждём оплаты по договорам</div>
          {!data ? <Empty text="Загрузка…" /> : data.awaiting.count === 0 ? <div style={{ color: '#4ade80', fontSize: 13 }}>✔ Неоплаченных сделок по договорам нет</div> : (
            <>
              {data.awaiting.byCompany.map((c) => (
                <Row key={c.company} left={<b>{c.company}</b>}
                  sub={`${c.count} сделок · старейший период ${c.oldest ? new Date(c.oldest).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }) : '—'} (${c.oldestDays} дн.)`}
                  right={<b style={{ color: '#fb923c' }}>{money(c.amount)}</b>} />
              ))}
              <div style={{ fontSize: 11, color: muted, margin: '12px 0 2px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Самые давние</div>
              {data.awaiting.items.map((s) => (
                <Row key={s.id} left={`№${s.number} · ${s.title}`}
                  sub={`${s.company} · ${s.stage} · ${s.days} дн.`} right={money(s.amount)} />
              ))}
            </>
          )}
        </div>

        {/* Застрявшие */}
        <div style={card}>
          <div style={sectionTitle}>Сделки без движения (3+ дня){data && data.stale.total > 6 ? ` · показаны 6 из ${data.stale.total}` : ''}</div>
          {!data ? <Empty text="Загрузка…" /> : data.stale.items.length === 0 ? <div style={{ color: '#4ade80', fontSize: 13 }}>✔ Застрявших сделок нет</div> :
            data.stale.items.map((s) => (
              <Row key={s.id} left={`№${s.number} ${s.title}`} sub={`${s.stage ?? ''} · без движения ${s.days} дн.`} right={money(s.amount)} />
            ))}
        </div>

        {/* Склад */}
        <div style={card}>
          <div style={sectionTitle}>Склад и ключи</div>
          {!data ? <Empty text="Загрузка…" /> : (data.stock.low.length === 0 && data.stock.keys.length === 0) ? <div style={{ color: '#4ade80', fontSize: 13 }}>✔ Остатков достаточно</div> : (
            <>
              {data.stock.low.map((s) => <Row key={`s${s.id}`} left={s.name} sub="остаток ниже минимума" right={<span style={{ color: '#f87171' }}>{s.available} (мин {s.min})</span>} />)}
              {data.stock.keys.map((s) => <Row key={`k${s.id}`} left={s.name} sub="ключей активации осталось" right={<span style={{ color: s.available === 0 ? '#f87171' : '#fbbf24' }}>{s.available} из {s.total}</span>} />)}
            </>
          )}
        </div>
      </div>

      {/* Интеграции */}
      <div style={sectionTitle}>Интеграции</div>
      <div className="dash-integrations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#d1d5db' }}>UKassa / Касса</span>
            <button onClick={loadUkassa} disabled={ukassaLoading} style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: 16, padding: 4, width: 'auto' }}>{ukassaLoading ? '…' : '↻'}</button>
          </div>
          {!ukassa ? <div style={{ color: '#374151', fontSize: 13 }}>Загрузка…</div> : ukassa.error ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Dot ok={false} /><span style={{ color: '#f87171', fontSize: 13 }}>{ukassa.error}</span></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Dot ok={ukassa.ok} /><span style={{ fontSize: 13, fontWeight: 600, color: ukassa.ok ? '#4ade80' : '#f87171' }}>{ukassa.ok ? 'Подключена' : 'Не подключена'}</span></div>
              {ukassa.userName && <div style={{ fontSize: 12, color: muted }}>Пользователь: {ukassa.userName}</div>}
              {ukassa.tokenExpires && <div style={{ fontSize: 12, color: '#fbbf24' }}>Токен до: {new Date(ukassa.tokenExpires).toLocaleDateString('ru-RU')}</div>}
              {ukassa.shiftText && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <Dot ok={ukassa.shiftOpen ?? false} /><span style={{ fontSize: 12, color: ukassa.shiftOpen ? '#4ade80' : '#fbbf24' }}>Смена: {ukassa.shiftText}</span>
                </div>
              )}
              {ukassa.cashboxName && <div style={{ fontSize: 12, color: muted }}>Касса: {ukassa.cashboxName}</div>}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#d1d5db' }}>Monttech / Ключи</span>
            <button onClick={loadMont} disabled={montLoading} style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: 16, padding: 4, width: 'auto' }}>{montLoading ? '…' : '↻'}</button>
          </div>
          {!mont ? <div style={{ color: '#374151', fontSize: 13 }}>Загрузка…</div> : mont.error ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Dot ok={false} /><span style={{ color: '#f87171', fontSize: 13 }}>{mont.error}</span></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#f9fafb', lineHeight: 1 }}>{mont.available?.toLocaleString('ru-RU', { minimumFractionDigits: 2 })} <span style={{ fontSize: 14, color: muted }}>₽</span></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Dot ok={!mont.blocked} /><span style={{ fontSize: 12, color: mont.blocked ? '#f87171' : '#4ade80' }}>{mont.blocked ? `Заблокирован: ${mont.blockReason}` : 'Счёт активен'}</span></div>
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ marginBottom: 14 }}><span style={{ fontSize: 13, fontWeight: 700, color: '#d1d5db' }}>Halyk / Оплата</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data?.halyk ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Dot ok={data.halyk.errors24h === 0} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: data.halyk.errors24h === 0 ? '#4ade80' : '#fbbf24' }}>
                    {data.halyk.errors24h === 0 ? 'Обмен без ошибок' : `Ошибок за 24 ч: ${data.halyk.errors24h}`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: muted }}>Запросов за 24 ч: {data.halyk.total24h}{data.halyk.lastOk ? ` · успешный: ${time(data.halyk.lastOk)}` : ''}</div>
                {data.halyk.lastError && <div style={{ fontSize: 12, color: '#f87171' }}>Последняя ошибка: {data.halyk.lastError.kind}, {time(data.halyk.lastError.at)}</div>}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Dot ok={true} /><span style={{ fontSize: 13, fontWeight: 600, color: '#4ade80' }}>Настроен</span></div>
                <div style={{ fontSize: 12, color: muted }}>Журнал обмена пока не собирается</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
